import { SolutionPage } from "@/components/shared/SolutionPage";
import { LeadPipelineMockup } from "@/components/shared/LeadPipelineMockup";
import { Zap, Repeat, Network, TrendingUp } from 'lucide-react';
import { Link } from "react-router-dom";
import { ServiceFAQ } from "@/components/shared/ServiceFAQ";

/**
 * Capability spoke: "Speed-to-Lead".
 * Single-intent page for "speed to lead" / "instant lead response" demand.
 * Hub is /platform; clustered to its parent layer /solutions/lead-generation-system.
 * Mirrors the MissedCallTextBack spoke pattern. Outcome-framed, no invented metrics.
 */
const SpeedToLead = () => {
  return (
    <SolutionPage
      breadcrumbTitle="Speed-to-Lead"
      seo={{
        title: "Speed-to-Lead: Respond to Every Lead in Seconds | Frayze",
        description: "The first business to respond wins the deal. Frayze auto-replies to every new lead (call, form, or chat) in seconds, so you beat competitors to the customer every time.",
        keywords: "speed to lead, instant lead response, automated lead response, lead response time, first responder advantage, respond to leads faster, 5 minute rule",
        canonical: "https://frayze.ca/platform/speed-to-lead/"
      }}
      hero={{
        badge: "Capability: Lead Response",
        badgeColor: "text-indigo-700",
        title: "The First to Respond Wins the Deal.",
        description: "Every new lead (call, form, or chat) gets an instant, personal reply in seconds. While competitors are still checking email, you've already started the conversation.",
        buttonColor: "bg-indigo-700 hover:bg-indigo-800",
        gradientParams: "from-indigo-500/5 via-background to-background",
        primaryCta: { text: "Respond Faster", link: "/book-consultation/?tier=foundation" },
        secondaryCta: { text: "See Pricing", link: "/pricing/" },
        visual: <LeadPipelineMockup />
      }}
      problem={{
        title: "Leads Don't Wait. Most Businesses Make Them.",
        description: (
          <>
            A new lead is hottest the second they raise their hand. An hour later they've moved on or bought from whoever answered first.
            <br /><br />
            <strong>The industry research is clear:</strong>
          </>
        ),
        gridCols: 3,
        cards: [
          { value: "5 min", label: "Wait longer to respond and your odds of qualifying the lead drop ~80%" },
          { value: "21x", label: "More likely to qualify a lead contacted in 5 minutes vs 30" },
          { value: "78%", label: "Of customers buy from the business that responds first" }
        ]
      }}
      systemModel={{
        title: "How Speed-to-Lead Works",
        description: "Every lead source feeds one system that responds instantly: day or night.",
        variant: "steps",
        connectionColor: "bg-gradient-to-r from-indigo-500/20 via-indigo-500/50 to-indigo-500/20",
        items: [
          { step: "1", title: "Lead Comes In", description: "Website form, missed call, Facebook lead, or chat. It all lands in one place." },
          { step: "2", title: "Instant Auto-Reply", description: "Within seconds the lead gets a personal SMS and email, not a generic autoresponder." },
          { step: "3", title: "Routed & Assigned", description: "The lead is qualified, tagged, and routed to the right rep with full context." },
          { step: "4", title: "Followed Up", description: "No reply? A multi-day nurture keeps working until they book or opt out." }
        ]
      }}
      layers={{
        title: "What Comes With Speed-to-Lead",
        description: "Instant response across every channel. Built and managed for you, not another tool you have to run.",
        theme: "indigo",
        items: [
          {
            title: "Instant Response Engine",
            icon: Zap,
            items: [
              "Fires on every lead source",
              "Personal SMS + email in seconds",
              "24/7, including nights and weekends"
            ]
          },
          {
            title: "Then It Keeps Working",
            icon: Repeat,
            items: [
              "Auto-routing to the right rep",
              "Multi-day nurture if there's no reply",
              "Every touch logged in the CRM"
            ]
          }
        ]
      }}
      interdependence={{
        title: "Speed Is Nothing Without a System Behind It",
        description: (
          <>
            Instant response only pays off if the lead is captured, routed, and nurtured. It runs on the <Link to="/solutions/lead-generation-system/" className="font-bold underline decoration-indigo-500/50 hover:text-indigo-600 transition-colors">Lead Generation System</Link>, is stored in the <Link to="/solutions/crm-automation/" className="font-bold underline decoration-teal-500/50 hover:text-teal-600 transition-colors">CRM</Link>, and can hand off to <Link to="/platform/" className="font-bold underline decoration-purple-500/50 hover:text-purple-600 transition-colors">the AI</Link> to answer and book.
            <br /><br />
            See where it sits in the wider <Link to="/platform/" className="font-bold underline decoration-indigo-500/50 hover:text-indigo-600 transition-colors">managed platform</Link>.
          </>
        ),
        icon: Network,
        quote: "Speed wins the lead. The system wins the sale."
      }}
      useCases={{
        title: "Where Speed-to-Lead Pays Off Most",
        items: [
          { title: "Car Dealerships", description: "AutoTrader, finance apps, and website inquiries answered before the shopper messages the next dealer.", link: "/automotive/" },
          { title: "Trades & Contractors", description: "Quote requests answered instantly, even from a job site.", link: "/industries/trades/" },
          { title: "High-Ticket Services", description: "When a lead is worth thousands, being first is the whole game.", link: "/solutions/lead-generation-system/" }
        ]
      }}
      socialProof={{
        title: "The operator playbook behind 400% lead growth",
        quote: "At Need A Car, instant lead response across six rooftops helped drive 400% inbound lead growth. That founder track record is the playbook Frayze installs for your business.",
        icon: TrendingUp,
        ctaText: "See the Need A Car story",
        ctaLink: "/case-studies/need-a-car-automotive-growth/"
      }}
      cta={{
        title: "Be the First Call Every Customer Gets Back",
        description: "Turn on instant lead response and stop losing deals to whoever answered faster.",
        primaryCta: { text: "Respond Faster", link: "/book-consultation/?tier=foundation" },
        secondaryCta: { text: "Get Your Free Lead-Response Audit", link: "/digital-audit/" },
        color: "bg-indigo-700 hover:bg-indigo-800"
      }}
      customSection={
        <div className="space-y-24 pb-24">
          <ServiceFAQ
            title="Speed-to-Lead: Common Questions"
            description="Straight answers on lead response time and what slow follow-up really costs."
            items={[
              {
                question: "What is speed-to-lead?",
                answer: "Speed-to-lead is how fast you respond to a new lead. It matters because the business that replies first usually wins: research shows responding within five minutes makes you many times more likely to qualify the lead than waiting even 30."
              },
              {
                question: "How fast should I respond to a new lead?",
                answer: "Within five minutes, ideally seconds. After five minutes your odds of qualifying the lead drop sharply, and most buyers go with whoever got back to them first. Frayze automates the first response so it is instant, every time."
              },
              {
                question: "Does this work for web forms and chats, not just calls?",
                answer: "Yes. Every lead source (website forms, missed calls, Facebook and Instagram, web chat, Google) feeds one system that auto-replies in seconds and routes the lead to the right person."
              },
              {
                question: "What happens if the lead doesn't reply to the first message?",
                answer: "A multi-day follow-up sequence takes over automatically (a mix of SMS and email) until the lead books, replies, or opts out. Most leads need several touches, and the system does them so your team does not have to remember."
              },
              {
                question: "Is the first reply just a generic autoresponder?",
                answer: "No. The message is personalized with the lead's name and what they asked about, sent from your business, so it reads like a real person got back to them fast."
              },
              {
                question: "Can AI handle the conversation, not just the first reply?",
                answer: "Yes. You can turn on Frayze Conversation AI to answer questions and book the appointment end to end, or keep a human in the loop and let the AI just draft replies."
              }
            ]}
          />
        </div>
      }
    />
  );
};

export default SpeedToLead;
